import { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Download, CheckCircle, Loader2, Sparkles, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAN_STORE_URL = "https://stan.store/srichburg/p/get-my-ai-survival-blueprint-now";

export default function LeadMagnetSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/subscribe`, {
        ...formData,
        source: "lead_magnet",
      });

      if (response.data.status === "success" || response.data.status === "already_subscribed") {
        setIsSuccess(true);
        toast.success(response.data.message);

        // Redirect to Stan Store in a new tab after a brief delay
        const redirectUrl = response.data.stan_store_url || STAN_STORE_URL;
        setTimeout(() => {
          window.open(redirectUrl, "_blank", "noopener,noreferrer");
        }, 800);
      }
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      ref={ref}
      id="lead-magnet"
      className="py-16 md:py-20 lg:py-24 px-6 md:px-12 lg:px-24 relative overflow-hidden"
      data-testid="lead-magnet-section"
    >
      {/* Large gradient background */}
      <div className="absolute inset-0 hero-gradient opacity-50" />
      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[600px] h-[600px] gradient-blob" />
      <div className="absolute left-0 bottom-0 w-[400px] h-[400px] gradient-blob" />

      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-8"
        >
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/50 bg-primary/10 mb-4">
            <Sparkles size={16} className="text-primary" />
            <span className="text-xs font-['Orbitron'] uppercase tracking-[0.15em] text-primary">
              Premium AI Blueprint — Instant Access
            </span>
          </div>

          <h2 className="font-['Orbitron'] text-3xl sm:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-foreground mb-3">
            Get The <span className="gradient-text">AI Survival Blueprint</span>
          </h2>
          <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto">
            A step-by-step guide to help individuals, families, students, professionals, and
            entrepreneurs prepare for the AI economy. Drop your email and we'll send you
            straight to the Blueprint.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="neon-card p-6 lg:p-10"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Left - Book Cover + Benefits */}
            <div>
              {/* Cover image with floating animation + glow */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9, rotateY: -15 }}
                animate={isInView ? { opacity: 1, scale: 1, rotateY: 0 } : {}}
                transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
                whileHover={{ scale: 1.03, rotateY: 5 }}
                className="relative mb-6 mx-auto max-w-[280px]"
                style={{ perspective: "1000px" }}
                data-testid="lead-magnet-cover"
              >
                {/* Glow behind cover */}
                <div className="absolute inset-0 -m-6 bg-gradient-to-tr from-primary/40 via-fuchsia-500/30 to-blue-500/40 blur-3xl rounded-3xl opacity-70" />
                {/* Cover image */}
                <motion.img
                  src="/ai-survival-blueprint-cover.png"
                  alt="AI Survival Blueprint by Shea Richburg — Book Cover"
                  className="relative w-full h-auto rounded-xl shadow-[0_20px_60px_-15px_rgba(168,85,247,0.6)] border border-primary/30"
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                />
                {/* "Preview" badge */}
                <div className="absolute -top-3 -right-3 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-[10px] font-['Orbitron'] uppercase tracking-widest font-bold shadow-lg">
                  What You Get
                </div>
              </motion.div>

              <ul className="space-y-3 mb-2">
                {[
                  "30+ Page Premium Digital Guide",
                  "AI Tool Directory (2026 Edition)",
                  "90-Day AI Action Plan",
                  "Income & Ownership Strategies",
                  "AI Career & Education Insights",
                  "AI Rights & Privacy Breakdown",
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-sm text-muted-foreground">
                    <CheckCircle size={18} className="text-primary flex-shrink-0 mt-0.5" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Right - Form */}
            <div>
              {!isSuccess ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                      Your Name
                    </label>
                    <Input
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="bg-background border-primary/30 focus:border-primary rounded-lg h-12 text-base text-foreground placeholder:text-muted-foreground"
                      data-testid="lead-magnet-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-semibold uppercase tracking-wider text-foreground mb-2 block">
                      Email Address
                    </label>
                    <Input
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="bg-background border-primary/30 focus:border-primary rounded-lg h-12 text-base text-foreground placeholder:text-muted-foreground"
                      data-testid="lead-magnet-email-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-primary text-primary-foreground rounded-full h-14 font-bold uppercase tracking-wider text-base hover:bg-primary/90 transition-all btn-glow"
                    data-testid="lead-magnet-submit-btn"
                  >
                    {isLoading ? (
                      <Loader2 className="animate-spin" size={22} />
                    ) : (
                      <>
                        <Download size={20} className="mr-3" />
                        Get The Blueprint Now
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-center text-muted-foreground">
                    No spam. Unsubscribe anytime. Your privacy is protected.
                  </p>
                </form>
              ) : (
                <div
                  className="text-center py-8 px-6 rounded-xl border border-primary/30 bg-primary/10"
                  data-testid="lead-magnet-success"
                >
                  <CheckCircle className="text-primary mx-auto mb-4" size={48} />
                  <h3 className="font-['Orbitron'] text-xl font-bold uppercase text-foreground mb-2">
                    You're In!
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    We've opened the AI Survival Blueprint in a new tab. If it didn't open,
                    click the button below. We've also emailed you the link.
                  </p>
                  <a
                    href={STAN_STORE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground rounded-full h-14 font-bold uppercase tracking-wider hover:bg-primary/90 transition-all btn-glow"
                    data-testid="lead-magnet-stan-store-btn"
                  >
                    <ExternalLink size={20} />
                    Open The Blueprint
                  </a>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
